/**
 * Annotation service: DSA/Girder annotation REST API.
 * Uses the existing api instance (Girder token sent automatically).
 */
import api from '../API';

const DEFAULT_ANNOTATION_NAME = 'Default';

/**
 * List annotations for an item.
 * @param {string} itemId - Girder item id
 * @returns {Promise<Array<{ _id: string, annotation: { name?, description?, elements? } }>>}
 */
export async function listAnnotations(itemId) {
    const response = await api.get('annotation', { query: { itemId }, cache: 'no-store' });
    if (response?.status !== 200) {
        throw new Error(response?.data?.message || 'Failed to list annotations');
    }
    const data = response.data;
    return Array.isArray(data) ? data : [];
}

/**
 * Get one annotation by id.
 * @param {string} annotationId - Annotation document id
 * @returns {Promise<{ _id: string, annotation: { name?, description?, elements? } }>}
 */
export async function getAnnotation(annotationId) {
    const response = await api.get(`annotation/${annotationId}`, { cache: 'no-store' });
    if (response?.status !== 200) {
        throw new Error(response?.data?.message || 'Failed to get annotation');
    }
    return response.data;
}

/**
 * Create a new annotation for an item.
 * @param {string} itemId - Girder item id
 * @param {{ name?: string, description?: string, elements?: object[] }} body - Annotation document body
 * @returns {Promise<{ _id: string, annotation: object }>}
 */
export async function createAnnotation(itemId, body = {}) {
    const payload = {
        name: body.name ?? DEFAULT_ANNOTATION_NAME,
        description: body.description ?? '',
        elements: body.elements ?? [],
    };
    const response = await api.post('annotation', { query: { itemId }, json: payload });
    if (response?.status !== 200) {
        throw new Error(response?.data?.message || 'Failed to create annotation');
    }
    return response.data;
}

/**
 * Update an existing annotation.
 * @param {string} annotationId - Annotation document id
 * @param {string} itemId - Girder item id
 * @param {{ name?: string, description?: string, elements?: object[] }} body - Annotation document body
 * @returns {Promise<{ _id: string, annotation: object }>}
 */
export async function updateAnnotation(annotationId, itemId, body = {}) {
    const payload = {
        name: body.name ?? DEFAULT_ANNOTATION_NAME,
        description: body.description ?? '',
        elements: body.elements ?? [],
    };
    const response = await api.put(`annotation/${annotationId}`, { query: { itemId }, json: payload });
    if (response?.status !== 200) {
        throw new Error(response?.data?.message || 'Failed to update annotation');
    }
    return response.data;
}
